/**
 * PHP API Auth Adapter — session via HttpOnly cookie (credentials: 'include').
 */

const API_BASE_URL = window.location.origin + '/api'

/** Legacy localStorage key — cleared on logout; auth no longer stored there. */
const LEGACY_TOKEN_KEY = 'auth_token'

export function getAuthToken(): string | null {
  return null
}

export function setAuthToken(_token: string): void {
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY)
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent('authTokenSet', { detail: {} }))
}

export function removeAuthToken(): void {
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

export async function getCurrentUser() {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      if (response.status === 401) {
        removeAuthToken()
        return null
      }
      throw new Error(`HTTP ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error getting current user:', error)
    return null
  }
}

export function initiateLogin() {
  window.location.replace(`${API_BASE_URL}/auth/login`)
}

export async function handleAuthCallback(_token?: string) {
  const user = await getCurrentUser()
  if (!user) {
    throw new Error('Session not established after login')
  }
  setAuthToken('')
  return user
}

export async function logout() {
  try {
    removeAuthToken()
    localStorage.clear()
    sessionStorage.clear()

    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
      })
    } catch (fetchError) {
      console.log('Logout request failed (ignored):', fetchError)
    }

    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations()
        for (const registration of registrations) {
          await registration.unregister()
        }
      } catch (error) {
        console.error('Error unregistering service workers:', error)
      }
    }

    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys()
        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
      } catch (error) {
        console.error('Error clearing caches:', error)
      }
    }
  } catch (error) {
    console.error('Error during logout:', error)
    removeAuthToken()
    localStorage.clear()
    sessionStorage.clear()
  } finally {
    window.location.replace('/')
  }
}

export async function verifyToken(): Promise<boolean> {
  const user = await getCurrentUser()
  return user !== null
}

export async function refreshToken(): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      if (response.status === 401) {
        removeAuthToken()
      }
      return null
    }

    const data = await response.json()
    if (data.success) {
      window.dispatchEvent(new CustomEvent('authTokenSet', { detail: {} }))
      return 'cookie'
    }
    return null
  } catch (error) {
    console.error('Error refreshing token:', error)
    return null
  }
}
